export const TemperatureUnit=Object.freeze({
  CELSIUS:'C',
  FAHRENHEIT:'F'
});

const CHAMBER_MIN_C=-100;
const CHAMBER_MAX_C=600;
const INTERNAL_MIN_C=-100;
const INTERNAL_MAX_C=300;
const MAX_DURATION_MINUTES=365*24*60;

function required(value,label,max=128){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  if(text.length>max)throw new TypeError(label+' is too long');
  return text;
}

function optionalNumber(value,label,{min,max}){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number)||number<min||number>max){
    throw new TypeError(label+' is outside the supported range');
  }
  return number;
}

function optionalDuration(value){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isInteger(number)||number<1||number>MAX_DURATION_MINUTES){
    throw new TypeError('plannedDurationMinutes must be a positive whole number of minutes');
  }
  return number;
}

function unit(value){
  const normalized=String(value||TemperatureUnit.CELSIUS).trim().toUpperCase();
  if(!Object.values(TemperatureUnit).includes(normalized)){
    throw new TypeError('temperature unit must be C or F');
  }
  return normalized;
}

function round(value,precision=3){
  const factor=10**precision;
  return Math.round((value+Number.EPSILON)*factor)/factor;
}

export function temperatureToCelsius(value,{unit:inputUnit='C'}={}){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number))throw new TypeError('temperature must be numeric');
  const u=unit(inputUnit);
  return round(u==='F'?((number-32)*5)/9:number);
}

export function temperatureFromCelsius(value,{unit:outputUnit='C',precision=1}={}){
  if(value==null||value==='')return null;
  const number=Number(value);
  if(!Number.isFinite(number))throw new TypeError('temperature must be numeric');
  const u=unit(outputUnit);
  return round(u==='F'?(number*9)/5+32:number,precision);
}

export function createStageTarget({
  stageId,
  userId,
  targetChamberCelsius=null,
  minChamberCelsius=null,
  maxChamberCelsius=null,
  plannedDurationMinutes=null
}={}){
  const target=optionalNumber(targetChamberCelsius,'targetChamberCelsius',{min:CHAMBER_MIN_C,max:CHAMBER_MAX_C});
  const min=optionalNumber(minChamberCelsius,'minChamberCelsius',{min:CHAMBER_MIN_C,max:CHAMBER_MAX_C});
  const max=optionalNumber(maxChamberCelsius,'maxChamberCelsius',{min:CHAMBER_MIN_C,max:CHAMBER_MAX_C});

  if(min!=null&&max!=null&&min>max)throw new TypeError('minChamberCelsius cannot exceed maxChamberCelsius');
  if(target!=null&&min!=null&&target<min)throw new TypeError('targetChamberCelsius cannot be below minChamberCelsius');
  if(target!=null&&max!=null&&target>max)throw new TypeError('targetChamberCelsius cannot exceed maxChamberCelsius');

  return Object.freeze({
    stageId:required(stageId,'stageId'),
    userId:required(userId,'userId'),
    targetChamberCelsius:target,
    minChamberCelsius:min,
    maxChamberCelsius:max,
    plannedDurationMinutes:optionalDuration(plannedDurationMinutes)
  });
}

export function isEmptyStageTarget(value={}){
  const target=createStageTarget(value);
  return target.targetChamberCelsius==null
    &&target.minChamberCelsius==null
    &&target.maxChamberCelsius==null
    &&target.plannedDurationMinutes==null;
}

export function stageTargetToRow(value){
  const target=createStageTarget(value);
  return Object.freeze({
    stage_id:target.stageId,
    user_id:target.userId,
    target_chamber_c:target.targetChamberCelsius,
    min_chamber_c:target.minChamberCelsius,
    max_chamber_c:target.maxChamberCelsius,
    planned_duration_minutes:target.plannedDurationMinutes
  });
}

export function stageTargetFromRow(row={}){
  return createStageTarget({
    stageId:row.stage_id,
    userId:row.user_id,
    targetChamberCelsius:row.target_chamber_c,
    minChamberCelsius:row.min_chamber_c,
    maxChamberCelsius:row.max_chamber_c,
    plannedDurationMinutes:row.planned_duration_minutes
  });
}

export function createStageItemTarget({
  stageId,
  itemId,
  userId,
  targetInternalCelsius
}={}){
  const target=optionalNumber(targetInternalCelsius,'targetInternalCelsius',{min:INTERNAL_MIN_C,max:INTERNAL_MAX_C});
  if(target==null)throw new TypeError('targetInternalCelsius is required for an item target');
  return Object.freeze({
    stageId:required(stageId,'stageId'),
    itemId:required(itemId,'itemId'),
    userId:required(userId,'userId'),
    targetInternalCelsius:target
  });
}

export function stageItemTargetToRow(value){
  const target=createStageItemTarget(value);
  return Object.freeze({
    stage_id:target.stageId,
    item_id:target.itemId,
    user_id:target.userId,
    target_internal_c:target.targetInternalCelsius
  });
}

export function stageItemTargetFromRow(row={}){
  return createStageItemTarget({
    stageId:row.stage_id,
    itemId:row.item_id,
    userId:row.user_id,
    targetInternalCelsius:row.target_internal_c
  });
}

export function presentStageTarget(value,{temperatureUnit='C'}={}){
  const target=createStageTarget(value);
  return Object.freeze({
    unit:unit(temperatureUnit),
    targetChamber:temperatureFromCelsius(target.targetChamberCelsius,{unit:temperatureUnit}),
    minChamber:temperatureFromCelsius(target.minChamberCelsius,{unit:temperatureUnit}),
    maxChamber:temperatureFromCelsius(target.maxChamberCelsius,{unit:temperatureUnit}),
    plannedDurationMinutes:target.plannedDurationMinutes
  });
}
